import { useRef, useState } from "react";
import { fileUrl } from "@/lib/api";
import { waUrl, chatNumber } from "@/lib/constants";
import { Star } from "lucide-react";

export function CredentialStrip({ brand }) {
  const mkts = (brand?.marketplaces || []).filter((m) => m.enabled);
  return (
    <div className="bg-[#0B3B2E] border-t-2 border-[#C9A34A] px-[4%] py-[3%] grid grid-cols-3 gap-2">
      <div className="min-w-0 flex items-center gap-2">
        {brand?.teaboard_logo_confirmed && brand?.teaboard_logo_url && (
          <div className="bg-[#F7F2E7] rounded p-[2px] shrink-0" style={{ height: "2.4em", width: "2.4em" }}>
            <img src={fileUrl(brand.teaboard_logo_url)} alt="Tea Board logo" className="h-full w-full object-contain" />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[#C9A34A] font-bold uppercase tracking-wider" style={{ fontSize: "0.55em" }}>
            Authorised Tea Distributor
          </div>
          <div className="text-[#F7F2E7]/85" style={{ fontSize: "0.5em" }}>Tea Board India Licence No.</div>
          <div className="text-[#F7F2E7] font-semibold font-mono-id" style={{ fontSize: "0.58em" }}>
            {brand?.licence_number || "TB|LC|DI-10237"}
          </div>
        </div>
      </div>
      <div className="min-w-0 flex flex-col items-center justify-center text-center">
        {brand?.show_google_rating !== false && (
          <>
            <div className="text-[#F7F2E7]/85 font-semibold uppercase tracking-wide" style={{ fontSize: "0.5em" }}>
              Google Rating
            </div>
            <div className="text-[#C9A34A] font-display font-bold flex items-center justify-center gap-1" style={{ fontSize: "1.1em" }}>
              <img src="/google-g.png" alt="Google" className="object-contain" style={{ height: "0.85em", width: "0.85em" }} />
              {brand?.google_rating || "4.8/5"}
            </div>
            <div className="flex gap-[2px]">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="fill-[#C9A34A] text-[#C9A34A]" style={{ width: "0.7em", height: "0.7em" }} />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[#C9A34A] font-bold uppercase tracking-wider mb-1" style={{ fontSize: "0.5em" }}>
          Our Presence on B2B Platforms
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {mkts.slice(0, 4).map((m) => (
            <div key={m.name} className="bg-white rounded px-1.5 py-1 flex items-center justify-center h-[2.1em] border border-[#C9A34A]/40 overflow-hidden">
              {m.logo_url ? (
                <img src={fileUrl(m.logo_url)} alt={m.name} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-[#0B3B2E] font-bold truncate" style={{ fontSize: "0.46em" }}>{m.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// WhatsApp pill colour options (mirrored in pdf_gen._PILL_STYLES)
export const PILL_STYLES = {
  green: { bg: "#25D366", hover: "#1FB855", fg: "#FFFFFF", icon: "#FFFFFF", label: "Green" },
  gold: { bg: "#C9A34A", hover: "#B8933F", fg: "#0B3B2E", icon: "#0B3B2E", label: "Gold" },
  white: { bg: "#F7F2E7", hover: "#EFE7D6", fg: "#0B3B2E", icon: "#25D366", label: "White" },
};

// Same WhatsApp-style glyph as pdf_gen._draw_wa_glyph (bubble + tail, handset cut in the pill colour)
export function WaGlyph({ color, hole, size = "1.45em" }) {
  return (
    <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }} aria-hidden>
      <circle cx="12.5" cy="11.5" r="9.5" fill={color} />
      <path d="M9.4 20.3 2.5 21.9l1.7-6.9z" fill={color} />
      <path d="M7.4 8.2a5.6 5.6 0 0 0 7.5 8.4" stroke={hole} strokeWidth="3" strokeLinecap="round" fill="none" />
    </svg>
  );
}

// Preset spots → CSS top-left offsets in PDF points (same numbers as pdf_gen.cover_page)
const NUM_BOX_PAD = { x: 8, y: 6 };
function presetStyle(pos, pt, edgeY, edgeX) {
  const [v, h] = pos.split("-");
  return {
    ...(v === "top" ? { top: pt(edgeY.top) } : { bottom: pt(edgeY.bottom) }),
    ...(h === "left" ? { left: pt(edgeX) } : h === "right" ? { right: pt(edgeX) } : { left: "50%", transform: "translateX(-50%)" }),
  };
}

export default function CoverPreview({ cat, brand, className = "", interactive = false, onPlacementChange, fixedNumber = false }) {
  const cover = cat?.cover || {};
  const img = fileUrl(cover.url);
  const t = cover.transform;
  const pl = cover.number_placement || {};
  const pos = pl.position || "top-center";
  const theme = pl.text_theme || "light";
  const backdrop = !!pl.needs_backdrop;
  const [pv, ph] = pos.split("-");
  const size = pl.size || "medium";
  // Sizes are PDF points (page = 720×900) expressed in container-query width units, so every cover preview
  // (editor, Preview tab, thumbnails) mirrors the export exactly. fixedNumber pins px for the 720px-wide Preview page.
  const pt = (v) => (fixedNumber ? `${v}px` : `${(v / 7.2).toFixed(3)}cqw`);
  const numFont = pt(size === "small" ? 17 : size === "large" ? 29 : 22);
  const phone = chatNumber(cat, brand);
  const pillStyle = PILL_STYLES[pl.chat_style] || PILL_STYLES.green;
  const chatHref = waUrl(phone, `Hello Prime Gold Tea, I am viewing Catalogue ${cat?.catalogue_number || ""} and would like to chat about your teas.`);
  // pill preset spot: AI-chosen chat_position, else same edge / opposite side (centred codes push it to the other edge)
  const chatPos = pl.chat_position && pl.chat_position !== pos ? pl.chat_position : (ph === "center" ? `${pv === "top" ? "bottom" : "top"}-center` : `${pv}-${ph === "left" ? "right" : "left"}`);

  // ---- free drag (editor only): positions stored as % of page width/height of the element's top-left ----
  const rootRef = useRef(null);
  const numRef = useRef(null);
  const pillRef = useRef(null);
  const dragRef = useRef(null);
  const [live, setLive] = useState({});
  const [guides, setGuides] = useState({ v: [], h: [] });
  const numXY = live.number_xy || pl.number_xy || null;
  const chatXY = live.chat_xy || pl.chat_xy || null;
  const SNAP = 1.2; // % of page — snap distance for guides
  const MARGINS = { number_xy: { x: 34 / 7.2, top: 24 / 9, bottom: 42 / 9 }, chat_xy: { x: 42 / 7.2, top: 34 / 9, bottom: 34 / 9 } };
  const startDrag = (key) => (e) => {
    if (!interactive || !onPlacementChange) return;
    e.preventDefault();
    e.stopPropagation();
    const cont = rootRef.current.getBoundingClientRect();
    const el = e.currentTarget.getBoundingClientRect();
    const otherEl = (key === "number_xy" ? pillRef : numRef).current?.getBoundingClientRect();
    const pct = (r) => r && { x: ((r.left - cont.left) / cont.width) * 100, y: ((r.top - cont.top) / cont.height) * 100, w: (r.width / cont.width) * 100, h: (r.height / cont.height) * 100 };
    dragRef.current = { key, cont, w: el.width, h: el.height, dx: e.clientX - el.left, dy: e.clientY - el.top, other: pct(otherEl) };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const moveDrag = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const w = (d.w / d.cont.width) * 100;
    const h = (d.h / d.cont.height) * 100;
    let x = Math.min(100 - w, Math.max(0, ((e.clientX - d.dx - d.cont.left) / d.cont.width) * 100));
    let y = Math.min(100 - h, Math.max(0, ((e.clientY - d.dy - d.cont.top) / d.cont.height) * 100));
    // snap targets: page centre, page margins (same as the preset corners), and the other overlay's centre/edges
    const m = MARGINS[d.key];
    const o = d.other;
    const xTargets = [
      { at: 50 - w / 2, line: 50 }, { at: m.x, line: m.x }, { at: 100 - m.x - w, line: 100 - m.x },
      ...(o ? [{ at: o.x + o.w / 2 - w / 2, line: o.x + o.w / 2 }, { at: o.x, line: o.x }, { at: o.x + o.w - w, line: o.x + o.w }] : []),
    ];
    const yTargets = [
      { at: 50 - h / 2, line: 50 }, { at: m.top, line: m.top }, { at: 100 - m.bottom - h, line: 100 - m.bottom },
      ...(o ? [{ at: o.y + o.h / 2 - h / 2, line: o.y + o.h / 2 }, { at: o.y, line: o.y }, { at: o.y + o.h - h, line: o.y + o.h }] : []),
    ];
    const v = [];
    const hh = [];
    const sx = xTargets.find((t) => Math.abs(x - t.at) < SNAP);
    if (sx) { x = Math.max(0, Math.min(100 - w, sx.at)); v.push(sx.line); }
    const sy = yTargets.find((t) => Math.abs(y - t.at) < SNAP);
    if (sy) { y = Math.max(0, Math.min(100 - h, sy.at)); hh.push(sy.line); }
    setGuides({ v, h: hh });
    setLive((s) => ({ ...s, [d.key]: { x, y, moved: true } }));
  };
  const endDrag = () => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    const v = live[d.key];
    if (v?.moved) onPlacementChange({ [d.key]: { x: Math.round(v.x * 100) / 100, y: Math.round(v.y * 100) / 100 } });
    setLive({});
    setGuides({ v: [], h: [] });
  };
  const dragProps = (key) => (interactive && onPlacementChange ? { onPointerDown: startDrag(key), onPointerMove: moveDrag, onPointerUp: endDrag, onPointerCancel: endDrag } : {});
  const freeStyle = (xy) => ({ left: `${xy.x}%`, top: `${xy.y}%` });

  const fit = t?.fit || "blur";
  const imgStyle = {
    objectFit: fit === "blur" ? "contain" : fit,
    transform: t ? `translate(${t.pos_x || 0}%, ${t.pos_y || 0}%) scale(${t.scale || 1}) rotate(${t.rotate || 0}deg)` : "none",
    transformOrigin: "center",
    transition: "transform 0.15s ease",
  };

  return (
    <div ref={rootRef} className={`relative w-full aspect-[4/5] bg-[#0B3B2E] overflow-hidden ${className}`} style={{ containerType: "inline-size" }} data-testid="cover-preview">
      {img && fit === "blur" && (
        <img
          src={img}
          alt=""
          aria-hidden
          data-testid="cover-blur-backdrop"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ filter: "blur(20px) brightness(0.72)", transform: `scale(1.12) rotate(${t?.rotate || 0}deg)` }}
        />
      )}
      {img ? (
        <img src={img} alt="cover" className="absolute inset-0 w-full h-full" style={imgStyle} draggable={false} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[#C9A34A]/60 text-sm">Upload your cover design</span>
        </div>
      )}

      {/* manual corner picker (editor only) — snaps the number back to a preset corner */}
      {interactive && img && (
        <div className="absolute inset-0 pointer-events-none" data-testid="cover-picker">
          {["top-left", "top-center", "top-right", "bottom-left", "bottom-center", "bottom-right"].map((z) => {
            const [zv, zh] = z.split("-");
            const style = {
              [zv === "bottom" ? "bottom" : "top"]: "4.5%",
              ...(zh === "center" ? { left: "50%", transform: "translateX(-50%)" } : zh === "left" ? { left: "5%" } : { right: "5%" }),
            };
            const active = z === pos && !numXY;
            return (
              <button
                key={z}
                type="button"
                data-testid={`cover-zone-${z}`}
                title={`Snap number ${z.replace("-", " ")}`}
                onClick={() => onPlacementChange && onPlacementChange({ position: z, number_xy: null, reason: "Placed manually" })}
                className={`absolute pointer-events-auto h-4 w-4 rounded-full border-2 transition-all ${active ? "bg-[#C9A34A] border-white scale-110 shadow" : "bg-white/40 border-white/80 hover:bg-[#C9A34A]/70 hover:scale-110"}`}
                style={style}
              />
            );
          })}
        </div>
      )}

      {/* snap guides while dragging */}
      {guides.v.map((x) => <div key={`v${x}`} className="absolute top-0 bottom-0 border-l border-dashed border-[#C9A34A] pointer-events-none" style={{ left: `${x}%` }} data-testid="cover-guide-v" />)}
      {guides.h.map((y) => <div key={`h${y}`} className="absolute left-0 right-0 border-t border-dashed border-[#C9A34A] pointer-events-none" style={{ top: `${y}%` }} data-testid="cover-guide-h" />)}

      {/* catalogue number — preset corner or free-dragged spot; hover shows the AI reason */}
      <span
        className={`absolute font-mono-id font-bold tracking-wide rounded-md whitespace-nowrap select-none ${interactive ? "cursor-move touch-none ring-1 ring-white/40 hover:ring-[#C9A34A]" : ""}`}
        ref={numRef}
        data-testid="cover-number"
        title={interactive ? "Drag to place the catalogue number anywhere" : pl.reason || undefined}
        {...dragProps("number_xy")}
        style={{
          ...(numXY ? freeStyle(numXY) : presetStyle(pos, pt, { top: 24, bottom: 42 }, 34)),
          fontSize: numFont,
          lineHeight: 1,
          padding: `${pt(NUM_BOX_PAD.y)} ${pt(NUM_BOX_PAD.x)}`,
          color: theme === "dark" ? "#0B3B2E" : "#E3C77C",
          background: backdrop ? (theme === "dark" ? "rgba(247,242,231,0.85)" : "rgba(11,59,46,0.72)") : "transparent",
          textShadow: backdrop ? "none" : (theme === "dark" ? "0 1px 3px rgba(255,255,255,0.6)" : "0 1px 4px rgba(0,0,0,0.7)"),
        }}
      >
        {cat?.catalogue_number || "PGT-CAT-…"}
      </span>

      {/* "Chat with us on WhatsApp" pill — preset corner or free-dragged spot, mirrors the PDF cover */}
      {chatHref && (
        <a
          href={chatHref}
          target="_blank"
          rel="noreferrer"
          ref={pillRef}
          data-testid="cover-chat-pill"
          draggable={false}
          onClick={(e) => interactive && e.preventDefault()}
          {...dragProps("chat_xy")}
          className={`absolute inline-flex items-center gap-1.5 rounded-full font-bold whitespace-nowrap shadow-[0_2px_6px_rgba(0,0,0,0.35)] select-none transition-colors ${interactive ? "cursor-move touch-none" : ""}`}
          onMouseEnter={(e) => { e.currentTarget.style.background = pillStyle.hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = pillStyle.bg; }}
          title={interactive ? "Drag to place the WhatsApp button anywhere" : undefined}
          style={{
            ...(chatXY ? freeStyle(chatXY) : presetStyle(chatPos, pt, { top: 34, bottom: 34 }, 42)),
            fontSize: pt(11),
            padding: `${pt(7)} ${pt(15)}`,
            background: pillStyle.bg,
            color: pillStyle.fg,
          }}
        >
          <WaGlyph color={pillStyle.icon} hole={pillStyle.bg} /> Tap to chat with us on WhatsApp{phone ? <span className="font-semibold opacity-90">· {phone}</span> : null}
        </a>
      )}

    </div>
  );
}
